// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/utils/connectorDefinitionsApi.js
//
// Thin wrapper around the /connector_definitions CRUD endpoints.
//
// Four calls:
//
//   listDefinitions({ appName, region })
//     GET /connector_definitions?app_name=<app>
//     Returns { definitions: [...] }.
//
//   getDefinition({ region, definitionId })
//     GET /connector_definitions?definition_id=<id>
//     Returns { definition: {...} }.
//
//   createOrUpdateDefinition({ region, appName, scope, definitionId, name, operationName, operationParams })
//     POST /connector_definitions
//     Returns { success: true, definition_id, created }.
//
//   deleteDefinition({ region, definitionId })
//     DELETE /connector_definitions?definition_id=<id>
//     Returns { success: true, definition_id } or throws.
//
// Every function throws on network / non-2xx / success:false, with
// the server's error message when one is available. Callers surface
// via ReusablePopup, matching every other admin surface.
//
// deleteDefinition's thrown Error additionally carries `.blockingRefs`
// when the server refused because of a reference guard (chain nodes
// or leadgen forms still using the definition). ConnectorDefinitionsPanel
// reads that to render a structured, two-section message rather than
// the server's flat string.

import { buildConnectorBase, getAdminToken } from "./connectorApi";

/**
 * Fetch every definition visible to an app.
 *
 * Server merges app-scoped rows (from the app's GSI partition) and
 * tenant-scoped rows (from a small scan). Each row carries a
 * `scope` field: "app" | "tenant".
 */
export async function listDefinitions({ appName, region }) {
  const params = new URLSearchParams();
  if (appName) params.set("app_name", appName);
  const qs = params.toString();

  const url =
    `${buildConnectorBase(region)}/connector_definitions` +
    (qs ? `?${qs}` : "");

  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAdminToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    // non-JSON body — handled below
  }

  if (!resp.ok || !body?.success) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to load connector definitions (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  return {
    definitions: Array.isArray(body.definitions) ? body.definitions : [],
  };
}

/**
 * Fetch one definition by id.
 */
export async function getDefinition({ region, definitionId }) {
  if (!definitionId) throw new Error("Missing definitionId");

  const url =
    `${buildConnectorBase(region)}/connector_definitions` +
    `?definition_id=${encodeURIComponent(definitionId)}`;

  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAdminToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {}

  if (!resp.ok || !body?.success || !body?.definition) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to load definition (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  return { definition: body.definition };
}

/**
 * Create or update a definition.
 *
 * `definitionId` omitted → create; provided → update.
 *
 * `scope` is "app" or "tenant". For "app" the caller must also
 * supply `appName`. For "tenant" `appName` is ignored and must be
 * omitted from the payload.
 */
export async function createOrUpdateDefinition({
  region,
  appName,
  scope = "app",
  definitionId,
  name,
  operationName,
  operationParams,
}) {
  if (!name) throw new Error("Missing name");
  if (!operationName) throw new Error("Missing operationName");
  if (scope === "app" && !appName) throw new Error("Missing appName for app scope");

  const payload = {
    scope,
    name,
    operation_name: operationName,
    operation_params: operationParams || {},
  };
  if (scope === "app") payload.app_name = appName;
  if (definitionId) payload.definition_id = definitionId;

  const url = `${buildConnectorBase(region)}/connector_definitions`;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAdminToken(),
    },
    body: JSON.stringify(payload),
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {}

  if (!resp.ok || !body?.success) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to save definition (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  return body;
}

/**
 * Delete a definition.
 *
 * The connector's delete guard refuses the operation when any chain
 * node or leadgen form still references the definition. On that
 * refusal the backend responds with:
 *
 *   {
 *     success: false,
 *     error: "<preformatted human message>",
 *     blocking_refs: {
 *       chains:        [{chain_id, name, enabled, where}, ...],
 *       leadgen_forms: [{app_name, form_uuid, form_name}, ...]
 *     }
 *   }
 *
 * This function throws with:
 *   .message      — the server's flat error string (always present
 *                   on a refusal; falls back to a generic string for
 *                   non-guard failures like 404 / 500 / network)
 *   .blockingRefs — the structured blocking_refs object, when present.
 *                   Absent on non-guard failures.
 *
 * Callers that want the structured form (ConnectorDefinitionsPanel
 * does) read `.blockingRefs`; callers that just want a message read
 * `.message`.
 */
export async function deleteDefinition({ region, definitionId }) {
  if (!definitionId) throw new Error("Missing definitionId");

  const url =
    `${buildConnectorBase(region)}/connector_definitions` +
    `?definition_id=${encodeURIComponent(definitionId)}`;

  const resp = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAdminToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {}

  if (!resp.ok || !body?.success) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to delete definition (HTTP ${resp.status})`;

    const err = new Error(msg);

    // Attach the structured refs so callers can format them without
    // re-parsing the message. Only present when the failure was a
    // reference-guard refusal — absent on 404 / 500 / network, which
    // is exactly when the caller should fall back to `err.message`.
    if (body?.blocking_refs && typeof body.blocking_refs === "object") {
      err.blockingRefs = body.blocking_refs;
    }

    throw err;
  }

  return body;
}